import { motion, AnimatePresence } from "framer-motion";
import { Archive, X, Loader2, Info, RotateCcw } from "lucide-react";

const AddToArchiveModal = ({ isOpen, onClose, onConfirm, deal, isLoading = false }) => {
  const dealTitle = deal?.title
    ? deal.title.length > 60
      ? `${deal.title.slice(0, 60)}…`
      : deal.title
    : null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[999] bg-black/50 backdrop-blur-sm"
            onClick={onClose}
            aria-hidden="true"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 12 }}
            transition={{ type: "spring", stiffness: 320, damping: 28 }}
            className="fixed inset-0 z-[1000] flex w-full items-center justify-center p-4 pointer-events-none"
          >
            <div
              className="pointer-events-auto w-full max-w-md min-w-0 rounded-2xl bg-white shadow-[0_25px_60px_-12px_rgba(0,0,0,0.28)] overflow-hidden box-border"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="archive-modal-title"
            >
              {/* Top accent stripe */}
              <div className="h-1 bg-gradient-to-r from-amber-400 via-amber-500 to-orange-500" />

              {/* Header */}
              <div className="px-6 pt-5 pb-4">
                <div className="flex gap-4 justify-between items-start">
                  <div className="flex items-center gap-3.5 min-w-0">
                    <div className="flex justify-center items-center w-11 h-11 bg-amber-50 rounded-xl border border-amber-100 shrink-0">
                      <Archive className="w-5 h-5 text-amber-600" strokeWidth={2} />
                    </div>
                    <div className="overflow-hidden min-w-0">
                      <h2
                        id="archive-modal-title"
                        className="text-lg font-bold leading-tight text-gray-900 break-words"
                      >
                        Add to Archive
                      </h2>
                      <p className="text-xs text-gray-500 mt-0.5 break-words">
                        Hide this deal from the consumer portal
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={isLoading}
                    className="p-1.5 shrink-0 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-50"
                    aria-label="Close"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Deal name card */}
              {dealTitle && (
                <div className="overflow-hidden px-6 pb-4 min-w-0">
                  <div className="flex items-start gap-3 px-4 py-3.5 bg-gray-50 rounded-xl border border-gray-200/80 min-w-0 overflow-hidden">
                    <div className="self-stretch w-1 bg-amber-400 rounded-full shrink-0" />
                    <div className="overflow-hidden flex-1 min-w-0">
                      <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 mb-1">
                        Deal
                      </p>
                      <p className="w-full text-sm font-semibold leading-snug text-gray-900 whitespace-normal break-words">
                        {dealTitle}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Info bullets */}
              <div className="px-6 pb-5 space-y-2.5 min-w-0 overflow-hidden">
                <div className="flex items-start gap-2.5 px-3.5 py-3 bg-amber-50 rounded-xl border border-amber-100 min-w-0 overflow-hidden">
                  <Info className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                  <p className="w-full min-w-0 text-xs leading-relaxed text-amber-700 whitespace-normal break-words">
                    Archived deals are{" "}
                    <strong>hidden from consumers</strong> and won't appear in
                    deal listings or search results.
                  </p>
                </div>
                <div className="flex items-start gap-2.5 px-3.5 py-3 bg-gray-50 rounded-xl border border-gray-200 min-w-0 overflow-hidden">
                  <RotateCcw className="w-4 h-4 text-gray-500 shrink-0 mt-0.5" />
                  <p className="w-full min-w-0 text-xs leading-relaxed text-gray-600 whitespace-normal break-words">
                    You can restore it later from the{" "}
                    <span className="font-semibold text-gray-800">
                      Deal Archive
                    </span>{" "}
                    currentPage.
                  </p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 px-6 pb-6">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={isLoading}
                  className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-all duration-150 disabled:opacity-60"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={onConfirm}
                  disabled={isLoading}
                  className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold text-white bg-amber-500 hover:bg-amber-600 active:bg-amber-700 transition-all duration-150 flex items-center justify-center gap-2 shadow-sm shadow-amber-200 disabled:opacity-70"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Archiving…
                    </>
                  ) : (
                    <>
                      <Archive className="w-4 h-4" />
                      Archive Deal
                    </>
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default AddToArchiveModal;
